import { HttpClient, HttpParams } from '@angular/common/http';
import { Type, inject } from '@angular/core';
import {
  BaseModel,
  mapToModel,
  toModelArray,
} from '@kinet-open-network/client/shared/data-access/model';
import {
  downloadBlob,
  getFilenameFromHeaders,
} from '@kinet-open-network/client/shared/util';
import {
  AbilityAction,
  AbilitySubject,
} from '@kinet-open-network/shared/data-access/model';
import { Observable, defaultIfEmpty, firstValueFrom, map } from 'rxjs';
import { ApiFilterBuilder } from './api-filter.builder';
import { ApiQuery } from './api-query.interface';
import { ApiPaginatedResponse, ApiResponse } from './api-response.interface';

export const apiQueryToHttpParams = (query?: ApiQuery | null): HttpParams => {
  let params = new HttpParams();
  const arrayLikes = ['fields', 'sort'];

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        if (arrayLikes.includes(key)) {
          if (Array.isArray(value)) {
            value.forEach((field) => {
              params = params.append(`${key}[]`, field);
            });
          }
          continue;
        }

        if (key.endsWith('[]') && arrayLikes.includes(key.slice(0, -2))) {
          continue;
        }

        if (value instanceof ApiFilterBuilder) {
          params = params.set(key, value.toString());
          continue;
        }

        params = params.set(key, value);
      }
    }
  }

  return params;
};

export abstract class BaseApiService<
  T extends BaseModel<unknown, AbilitySubject>,
  K extends keyof T = 'id',
> {
  protected http = inject(HttpClient);

  /**
   * The model class which should be used to map the API response to.
   */
  abstract model: Type<T>;

  /**
   * The endpoint of the API resource.
   */
  abstract get endpoint(): string;

  /**
   * Specify a query which should be appended to every query/export request.
   * When a function is provided, it will be called with the current query and
   * should return the updated query.
   *
   * Example:
   * ```ts
   * protected appendQuery: ApiQuery = {
   *  filters: `state eq "${OrderState.new}"`,
   * };
   * ```
   */
  protected appendQuery?:
    | ApiQuery
    | ((query?: ApiQuery | null) => ApiQuery | undefined);

  findOne(id: T[K]): Observable<T> {
    return this.http
      .get<ApiResponse<T>>(`${this.endpoint}/${this.encodeId(id)}`)
      .pipe(mapToModel(this.model, 'data'));
  }

  query(query?: ApiQuery | null): Observable<ApiPaginatedResponse<T>> {
    return this.http
      .get<ApiPaginatedResponse<T>>(this.endpoint, {
        params: this.apiQueryToHttpParams(query),
      })
      .pipe(
        map(({ data, ...response }) => ({
          ...response,
          data: toModelArray(data, this.model),
        })),
      );
  }

  create(data: Partial<T>): Observable<T> {
    return this.http
      .post<ApiResponse<T>>(this.endpoint, data)
      .pipe(mapToModel(this.model, 'data'));
  }

  update(id: T[K], data: Partial<T>): Observable<T> {
    return this.http
      .put<ApiResponse<T>>(`${this.endpoint}/${this.encodeId(id)}`, data)
      .pipe(mapToModel(this.model, 'data'));
  }

  delete(id: T[K]): Observable<boolean> {
    return this.http
      .delete<ApiResponse<T>>(`${this.endpoint}/${this.encodeId(id)}`)
      .pipe(map(() => true));
  }

  async export(query?: ApiQuery | null, filename?: string): Promise<void> {
    const response = await firstValueFrom(
      this.http.get(`${this.endpoint}/export`, {
        params: this.apiQueryToHttpParams(query),
        responseType: 'blob',
        observe: 'response',
      }),
    );

    const blob = response.body as Blob;

    if (filename === undefined) {
      filename = getFilenameFromHeaders(response.headers);
    }

    return downloadBlob(
      blob,
      filename ??
        `export-${this.model.name.toLowerCase()}s-${new Date().getTime()}.csv`,
    );
  }

  patchByQuery(query: ApiQuery | null, data: Partial<T>) {
    return this.http.patch<ApiPaginatedResponse<T>>(
      `${this.endpoint}/batch`,
      data,
      {
        params: this.apiQueryToHttpParams(query),
      },
    );
  }

  can(id: T[K]): Observable<Record<AbilityAction, boolean>> {
    return this.http
      .get<ApiResponse<Record<AbilityAction, boolean>>>(
        `${this.endpoint}/${this.encodeId(id)}/can`,
      )
      .pipe(
        defaultIfEmpty({ data: {} as Record<AbilityAction, boolean> }),
        map((res) => res.data),
      );
  }

  protected apiQueryToHttpParams(query?: ApiQuery | null): HttpParams {
    let mergedQuery: ApiQuery | undefined = { ...query };

    if (this.appendQuery) {
      if (typeof this.appendQuery === 'function') {
        mergedQuery = this.appendQuery(query);
      } else {
        mergedQuery = {
          ...query,
          ...this.appendQuery,
        };

        // Merge filters - user can provide filters in both query and appendQuery
        if (this.appendQuery.filters) {
          mergedQuery.filters = query?.filters
            ? `${query.filters} and (${this.appendQuery.filters})`
            : this.appendQuery.filters;
        }
      }
    }

    return apiQueryToHttpParams(mergedQuery);
  }

  encodeId(id: T[K]): string {
    return encodeURIComponent(id as any);
  }
}
